import { findAccountGateway, gatewayMessageMatches, cleanGatewayMac } from './gatewayStatus';
import { getGatewayMqttTopic } from './mqttDefaults';

/** Merge account gateway row into a canvas kGateway node (fills missing MAC/topic/creds). */
export function resolveGatewayNodeData(nodeData = {}, gateways = []) {
  const linked = findAccountGateway(gateways, {
    gatewayId: nodeData.gatewayId || nodeData.id,
    macAddress: nodeData.mac_address,
  });
  if (!linked) return { ...nodeData };

  const placeholderName = !nodeData.name || nodeData.name === 'KGateway' || nodeData.name === 'Gateway';

  return {
    ...nodeData,
    nodeType: 'kGateway',
    gatewayId: nodeData.gatewayId || linked.id,
    id: nodeData.id || linked.id,
    name: placeholderName ? linked.name : nodeData.name,
    mac_address: nodeData.mac_address || linked.mac_address || linked.gateway_mac_hex,
    mqtt_topic: nodeData.mqtt_topic || linked.mqtt_topic,
    mqtt_broker_host: nodeData.mqtt_broker_host || linked.mqtt_broker_host,
    mqtt_broker_port: nodeData.mqtt_broker_port || linked.mqtt_broker_port,
    mqtt_username: nodeData.mqtt_username || linked.mqtt_username,
    mqtt_password: nodeData.mqtt_password || linked.mqtt_password,
    tbmq_username: nodeData.tbmq_username || linked.tbmq_username,
    tbmq_password: nodeData.tbmq_password || linked.tbmq_password,
    mqtt_client_id: nodeData.mqtt_client_id || linked.mqtt_client_id,
    status: nodeData.status || linked.status,
    ip_address: nodeData.ip_address || linked.ip_address,
  };
}

/** Walk edges upstream to find the nearest gateway source node. */
export function findUpstreamGatewayNode(nodeId, nodes = [], edges = [], visited = new Set()) {
  if (!nodeId || visited.has(nodeId)) return null;
  visited.add(nodeId);

  const self = nodes.find((n) => n.id === nodeId);
  if (self && (self.type === 'gateway' || self.data?.nodeType === 'kGateway')) {
    return self;
  }

  const incoming = edges.filter((e) => e.target === nodeId);
  for (const edge of incoming) {
    const found = findUpstreamGatewayNode(edge.source, nodes, edges, visited);
    if (found) return found;
  }
  return null;
}

export function getUpstreamGatewayMessages(nodeId, nodes = [], edges = [], liveMessages = []) {
  const gatewayNode = findUpstreamGatewayNode(nodeId, nodes, edges);
  if (!gatewayNode) return { gatewayNode: null, messages: [], topic: '', label: 'gateway' };

  const data = gatewayNode.data || {};
  const topic = getGatewayMqttTopic(data);
  const mac = data.mac_address || data.gateway_mac_hex;

  const messages = (liveMessages || []).filter((msg) =>
    gatewayMessageMatches(msg, mac, topic),
  );

  return {
    gatewayNode,
    messages,
    topic,
    label: data.name || data.title || data.label || 'gateway',
  };
}

/** Find the nearest upstream node with a given nodeType (e.g. jsonParser before mapper). */
export function findUpstreamNodeOfType(nodeId, targetType, nodes = [], edges = [], visited = new Set()) {
  if (!nodeId || !targetType || visited.has(nodeId)) return null;
  visited.add(nodeId);

  const self = nodes.find((n) => n.id === nodeId);
  if (self?.data?.nodeType === targetType) return self;

  const incoming = edges.filter((e) => e.target === nodeId);
  for (const edge of incoming) {
    const found = findUpstreamNodeOfType(edge.source, targetType, nodes, edges, visited);
    if (found) return found;
  }
  return null;
}

function mapMessagesWithJsonPath(messages, jsonPath) {
  if (!jsonPath) return messages;
  return messages.map((msg) => {
    const raw = msg.payload ?? msg.data?.payload ?? msg;
    const extracted = applyJsonPathPreview(raw, jsonPath);
    return {
      ...msg,
      payload: extracted,
      _preview: { source: 'jsonParser', jsonPath },
    };
  });
}

/** Client-side JSONPath preview (matches backend jsonParser $.a.b.c). */
export function applyJsonPathPreview(payload, jsonPath) {
  if (!jsonPath || !jsonPath.startsWith('$.')) return payload;
  const parts = jsonPath.slice(2).split('.').filter(Boolean);
  let result = payload;
  for (const part of parts) {
    if (result != null && typeof result === 'object' && !Array.isArray(result)) {
      result = result[part];
    } else {
      break;
    }
  }
  return result;
}

export function previewMessagesForNode(nodeId, nodeType, config, nodes, edges, liveMessages) {
  const upstream = getUpstreamGatewayMessages(nodeId, nodes, edges, liveMessages);
  const { gatewayNode, messages, label } = upstream;
  if (!gatewayNode || !messages.length) {
    return { ...upstream, previewMessages: [] };
  }

  if (nodeType === 'jsonParser' && config?.jsonPath) {
    return {
      ...upstream,
      previewMessages: mapMessagesWithJsonPath(messages, config.jsonPath),
    };
  }

  if (nodeType === 'mapper' || nodeType === 'filter') {
    const jsonUpstream = findUpstreamNodeOfType(nodeId, 'jsonParser', nodes, edges);
    const jsonPath = jsonUpstream?.data?.jsonPath || config?.jsonPath;
    if (jsonPath) {
      return {
        ...upstream,
        previewMessages: mapMessagesWithJsonPath(messages, jsonPath),
      };
    }
  }

  return { ...upstream, previewMessages: messages };
}

export function isOrphanGatewayStub(node, gateways = []) {
  if (!node) return false;
  const isGateway = node.type === 'gateway' || node.data?.nodeType === 'kGateway';
  if (!isGateway) return false;
  const resolved = resolveGatewayNodeData(node.data || {}, gateways);
  if (getGatewayMqttTopic(resolved)) return false;
  const label = resolved.name || resolved.title || resolved.label || node.data?.title || node.data?.name;
  return /^node_\d+$/i.test(node.id) || label === 'KGateway' || label === 'Gateway';
}

/** Assign account gateways to canvas kGateway nodes before Run. */
export function autoFixGatewayNodes(nodes = [], gateways = []) {
  if (!Array.isArray(nodes) || !gateways.length) return nodes;

  const usedGatewayIds = new Set();
  nodes.forEach((node) => {
    const gid = node.data?.gatewayId;
    if (gid && getGatewayMqttTopic(resolveGatewayNodeData(node.data, gateways))) {
      usedGatewayIds.add(String(gid));
    }
  });

  return nodes.map((node) => {
    const isGateway = node.type === 'gateway' || node.data?.nodeType === 'kGateway';
    if (!isGateway) return node;

    let data = resolveGatewayNodeData(node.data || {}, gateways);

    if (!getGatewayMqttTopic(data)) {
      if (gateways.length === 1) {
        data = resolveGatewayNodeData({ ...data, gatewayId: gateways[0].id }, gateways);
      } else {
        const candidate = gateways.find(
          (g) => (g.mac_address || g.gateway_mac_hex) && !usedGatewayIds.has(String(g.id)),
        );
        if (candidate) {
          data = resolveGatewayNodeData({ ...data, gatewayId: candidate.id }, gateways);
          usedGatewayIds.add(String(candidate.id));
        }
      }
    } else if (data.gatewayId) {
      usedGatewayIds.add(String(data.gatewayId));
    }

    const macClean = (data.mac_address || data.gateway_mac_hex || '')
      .toString()
      .replace(/[^0-9A-Fa-f]/g, '')
      .toUpperCase();

    return {
      ...node,
      type: 'gateway',
      id: macClean ? `gateway_${macClean}` : node.id,
      data: {
        ...data,
        nodeType: 'kGateway',
        title: data.name || data.title || node.data?.title,
        label: data.name || data.label || node.data?.label,
      },
    };
  });
}

/** Drop empty sidebar KGateway stubs when a linked gateway exists on canvas. */
export function pruneOrphanGatewayStubs(nodes = [], gateways = []) {
  if (!gateways.length) return nodes;

  const hasLinkedGateway = nodes.some((n) => {
    const isGw = n.type === 'gateway' || n.data?.nodeType === 'kGateway';
    return isGw && !isOrphanGatewayStub(n, gateways);
  });

  if (!hasLinkedGateway) return nodes;

  return nodes.filter((n) => !isOrphanGatewayStub(n, gateways));
}

/** Remove duplicate canvas nodes that reference the same account gateway. */
export function dedupeGatewayNodes(nodes = [], gateways = []) {
  const seen = new Set();
  return nodes.filter((node) => {
    const isGw = node.type === 'gateway' || node.data?.nodeType === 'kGateway';
    if (!isGw) return true;
    const resolved = resolveGatewayNodeData(node.data || {}, gateways);
    const key = resolved.gatewayId
      || cleanGatewayMac(resolved.mac_address || resolved.gateway_mac_hex)
      || node.id;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function gatewayDedupeKey(node, gateways) {
  const resolved = resolveGatewayNodeData(node.data || {}, gateways);
  return resolved.gatewayId
    || cleanGatewayMac(resolved.mac_address || resolved.gateway_mac_hex)
    || node.id;
}

/** Remap edges when gateway node ids change (e.g. node_0 → gateway_AABBCC). */
export function remapEdgesForNodeIdChange(edges = [], oldId, newId) {
  if (!oldId || !newId || oldId === newId) return edges;
  return edges.map((e) => ({
    ...e,
    source: e.source === oldId ? newId : e.source,
    target: e.target === oldId ? newId : e.target,
  }));
}

/** Prune orphan stubs, auto-link gateways, dedupe, remap edges after id changes. */
export function prepareCanvasForRun(nodes = [], edges = [], gateways = []) {
  let nextNodes = autoFixGatewayNodes(nodes, gateways);
  let nextEdges = [...edges];

  nodes.forEach((oldNode, index) => {
    const newNode = nextNodes[index];
    if (newNode && oldNode.id !== newNode.id) {
      nextEdges = remapEdgesForNodeIdChange(nextEdges, oldNode.id, newNode.id);
    }
  });

  const seenGateways = new Map();
  const deduped = [];
  nextNodes.forEach((node) => {
    const isGw = node.type === 'gateway' || node.data?.nodeType === 'kGateway';
    if (!isGw) {
      deduped.push(node);
      return;
    }
    const key = gatewayDedupeKey(node, gateways);
    const existing = seenGateways.get(key);
    if (existing) {
      nextEdges = remapEdgesForNodeIdChange(nextEdges, node.id, existing.id);
      return;
    }
    seenGateways.set(key, node);
    deduped.push(node);
  });
  nextNodes = deduped;

  const linkedGateway = nextNodes.find(
    (n) => (n.type === 'gateway' || n.data?.nodeType === 'kGateway')
      && !isOrphanGatewayStub(n, gateways),
  );
  if (linkedGateway) {
    nextNodes.forEach((node) => {
      if (!isOrphanGatewayStub(node, gateways)) return;
      nextEdges = remapEdgesForNodeIdChange(nextEdges, node.id, linkedGateway.id);
    });
    nextNodes = nextNodes.filter((n) => !isOrphanGatewayStub(n, gateways));
  }

  const nodeIds = new Set(nextNodes.map((n) => n.id));
  nextEdges = nextEdges.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target));

  return { nodes: nextNodes, edges: nextEdges };
}
